import React, { useState, useEffect } from "react";
import { TextField, Select, Button } from "../ui";
import { FeatherChevronDown } from "@subframe/core";
import { getAllCustomers, getCustomerById } from "../services/customerApi";
import { journalsAPI } from "../services/api";
import {
  SearchableDropdown,
  BankGlAccountDropdown,
} from "../shared/presentation/components";
import { useDropdownCache } from "../shared/presentation/hooks";
import { GLMappingEntity } from "../shared/domain/entities/GLMappingEntity";

interface CustomerDepositFromOtherAccountsRecoProps {
  transactionDate: string;
  bankAccountId?: string;
  onDateChange: (date: string) => void;
  onPaymentModeChange: (paymentMode: string) => void;
  onFromAccountChange: (accountId: string) => void;
  onReceivedFromChange: (customerId: string) => void;
  onReferenceNumberChange: (refNumber: string) => void;
  zohoGl?: string;
}

const CustomerDepositFromOtherAccountsReco: React.FC<
  CustomerDepositFromOtherAccountsRecoProps
> = ({
  transactionDate,
  bankAccountId,
  onDateChange,
  onPaymentModeChange,
  onFromAccountChange,
  onReceivedFromChange,
  onReferenceNumberChange,
  zohoGl,
}) => {
  const [date, setDate] = useState<string>("");
  const [paymentMode, setPaymentMode] = useState<string>("Bank Transfer");
  const [fromAccount, setFromAccount] = useState<string>("");
  const [receivedFrom, setReceivedFrom] = useState<string>("");
  const [referenceNumber, setReferenceNumber] = useState<string>("");
  const [selectedAccountName, setSelectedAccountName] = useState<string>("");
  const [selectedCustomerName, setSelectedCustomerName] = useState<string>(
    "Select customer (optional)"
  );

  const { getCached, setCached } = useDropdownCache();

  // Cache for selected customers (to avoid refetching)
  const [customersCache, setCustomersCache] = useState<Map<string, any>>(
    new Map()
  );

  // Initialize date with transaction date
  useEffect(() => {
    if (transactionDate) {
      const formattedDate = new Date(transactionDate)
        .toISOString()
        .split("T")[0];
      setDate(formattedDate);
      onDateChange(formattedDate);
    }
  }, [transactionDate, onDateChange]);

  // Initialize payment mode
  useEffect(() => {
    onPaymentModeChange(paymentMode);
  }, [paymentMode, onPaymentModeChange]);

  useEffect(() => {
    if (!zohoGl) {
      setFromAccount("");
      setSelectedAccountName("");
      return;
    }

    setFromAccount(zohoGl);
    onFromAccountChange(zohoGl);

    journalsAPI
      .getGLMapByAccountId(zohoGl)
      .then((response) => {
        if (response.success && response.data) {
          const accountIdStr = String(response.data.account_id);
          setFromAccount(accountIdStr);
          setSelectedAccountName(response.data.gl_name ?? "");
          onFromAccountChange(accountIdStr);
        }
      })
      .catch((error) => {
        console.error("Failed to fetch GL account by ID:", error);
      });
  }, [zohoGl, onFromAccountChange]);

  const handleDateChange = (newDate: string) => {
    setDate(newDate);
    onDateChange(newDate);
  };

  const handlePaymentModeChange = (mode: string) => {
    setPaymentMode(mode);
    onPaymentModeChange(mode);
  };

  const handleFromAccountChange = (account: GLMappingEntity) => {
    setFromAccount(String(account.account_id));
    setSelectedAccountName(account.gl_name);
    onFromAccountChange(String(account.account_id));
  };

  const handleReceivedFromChange = (customer: any) => {
    const customerId = customer.zoho_customerid;
    setReceivedFrom(customerId);
    setSelectedCustomerName(customer.customer_name);
    onReceivedFromChange(customerId);

    // Cache the selected customer for future use
    setCustomersCache((prev) => {
      const newCache = new Map(prev);
      newCache.set(customerId, customer);
      return newCache;
    });

    // Cache the selected customer in the dropdown cache
    const cacheKey = { searchTerm: "", limit: 20, offset: 0 };
    const cached = getCached<{
      items: any[];
      hasMore?: boolean;
    }>("customers", cacheKey);

    if (cached) {
      // Check if customer is already in cache
      const hasCustomer = cached.items.some(
        (c: any) => c.zoho_customerid === customerId
      );
      if (!hasCustomer) {
        // Prepend selected customer to cached results
        setCached("customers", cacheKey, {
          items: [customer, ...cached.items],
          hasMore: cached.hasMore,
        });
      }
    }
  };

  // Fetch customers async function for SearchableDropdown
  const fetchCustomers = async (
    searchTerm: string,
    limit: number,
    offset: number,
    selectedCustomerId?: string,
    selectedCustomerName?: string
  ): Promise<{
    items: any[];
    hasMore?: boolean;
  }> => {
    // Only cache the initial load (offset 0, no search)
    const shouldCache = offset === 0 && !searchTerm;
    const cacheKey = { searchTerm, limit, offset };

    if (shouldCache) {
      const cached = getCached<{
        items: any[];
        hasMore?: boolean;
      }>("customers", cacheKey);

      if (cached) {
        // Always ensure selected customer is in the cached results (if provided)
        if (offset === 0 && selectedCustomerId && selectedCustomerName) {
          const hasSelected = cached.items.some(
            (customer: any) => customer.zoho_customerid === selectedCustomerId
          );

          if (!hasSelected) {
            // Check customers cache first (pre-fetched customers)
            const cachedCustomer = customersCache.get(selectedCustomerId);
            if (cachedCustomer) {
              return {
                items: [cachedCustomer, ...cached.items],
                hasMore: cached.hasMore,
              };
            }

            // Try to fetch selected customer from backend
            try {
              const selectedResponse = await getCustomerById(
                selectedCustomerId
              );
              if (selectedResponse.data) {
                // Map the response data to customer format
                const mappedCustomer: any = {
                  zoho_customerid: selectedResponse.data.zoho_customerid,
                  customer_id: selectedResponse.data.customer_id,
                  customer_name: selectedResponse.data.customer_name,
                  company_name: selectedResponse.data.customer_name,
                  email: selectedResponse.data.contact_email_1 || "",
                  phone: selectedResponse.data.contact_number_1 || "",
                  status: selectedResponse.data.status,
                  billing_address: selectedResponse.data.billing_address,
                  shipping_address: selectedResponse.data.shipping_address,
                  currency_code: selectedResponse.data.currency_id || "",
                  payment_terms: selectedResponse.data.payment_terms,
                  credit_limit: selectedResponse.data.credit_limit || 0,
                  created_at: selectedResponse.data.created_at,
                  updated_at: selectedResponse.data.updated_at,
                };
                // Store in cache for future use
                setCustomersCache((prev) => {
                  const newCache = new Map(prev);
                  newCache.set(selectedCustomerId, mappedCustomer);
                  return newCache;
                });
                return {
                  items: [mappedCustomer, ...cached.items],
                  hasMore: cached.hasMore,
                };
              }
            } catch (error) {
              console.warn(
                "Could not fetch selected customer from backend, using synthetic item:",
                error
              );
            }

            // Fallback: Create synthetic customer
            const syntheticCustomer: any = {
              zoho_customerid: selectedCustomerId,
              customer_id: selectedCustomerId,
              customer_name: selectedCustomerName,
              company_name: selectedCustomerName,
              email: "",
              phone: "",
              status: "active",
              billing_address: null,
              shipping_address: null,
              currency_code: "",
              payment_terms: "",
              credit_limit: 0,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            };
            return {
              items: [syntheticCustomer, ...cached.items],
              hasMore: cached.hasMore,
            };
          } else {
            // Selected customer exists in cache, move it to top for better UX
            const selectedIndex = cached.items.findIndex(
              (customer: any) => customer.zoho_customerid === selectedCustomerId
            );
            if (selectedIndex > 0) {
              const itemsCopy = [...cached.items];
              const [selectedCustomer] = itemsCopy.splice(selectedIndex, 1);
              return {
                items: [selectedCustomer, ...itemsCopy],
                hasMore: cached.hasMore,
              };
            }
          }
        }
        return cached;
      }
    }

    try {
      const response = await getAllCustomers({
        search: searchTerm || undefined,
        limit,
        offset,
      });

      let items = response.data || [];

      // Always ensure selected customer is in results (if provided)
      if (offset === 0 && selectedCustomerId && selectedCustomerName) {
        const hasSelected = items.some(
          (customer: any) => customer.zoho_customerid === selectedCustomerId
        );

        if (!hasSelected) {
          // Check customers cache first
          const cachedCustomer = customersCache.get(selectedCustomerId);
          if (cachedCustomer) {
            items = [cachedCustomer, ...items];
          } else {
            // Try to fetch selected customer from backend
            try {
              const selectedResponse = await getCustomerById(
                selectedCustomerId
              );
              if (selectedResponse.data) {
                // Map the response data to customer format
                const mappedCustomer: any = {
                  zoho_customerid: selectedResponse.data.zoho_customerid,
                  customer_id: selectedResponse.data.customer_id,
                  customer_name: selectedResponse.data.customer_name,
                  company_name: selectedResponse.data.customer_name,
                  email: selectedResponse.data.contact_email_1 || "",
                  phone: selectedResponse.data.contact_number_1 || "",
                  status: selectedResponse.data.status,
                  billing_address: selectedResponse.data.billing_address,
                  shipping_address: selectedResponse.data.shipping_address,
                  currency_code: selectedResponse.data.currency_id || "",
                  payment_terms: selectedResponse.data.payment_terms,
                  credit_limit: selectedResponse.data.credit_limit || 0,
                  created_at: selectedResponse.data.created_at,
                  updated_at: selectedResponse.data.updated_at,
                };
                // Store in cache for future use
                setCustomersCache((prev) => {
                  const newCache = new Map(prev);
                  newCache.set(selectedCustomerId, mappedCustomer);
                  return newCache;
                });
                items = [mappedCustomer, ...items];
              } else {
                // Fallback: Create synthetic customer
                const syntheticCustomer: any = {
                  zoho_customerid: selectedCustomerId,
                  customer_id: selectedCustomerId,
                  customer_name: selectedCustomerName,
                  company_name: selectedCustomerName,
                  email: "",
                  phone: "",
                  status: "active",
                  billing_address: null,
                  shipping_address: null,
                  currency_code: "",
                  payment_terms: "",
                  credit_limit: 0,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                };
                items = [syntheticCustomer, ...items];
              }
            } catch (error) {
              console.warn(
                "Could not fetch selected customer from backend, using synthetic item:",
                error
              );
              // Fallback: Create synthetic customer
              const syntheticCustomer: any = {
                zoho_customerid: selectedCustomerId,
                customer_id: selectedCustomerId,
                customer_name: selectedCustomerName,
                company_name: selectedCustomerName,
                email: "",
                phone: "",
                status: "active",
                billing_address: null,
                shipping_address: null,
                currency_code: "",
                payment_terms: "",
                credit_limit: 0,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              };
              items = [syntheticCustomer, ...items];
            }
          }
        } else {
          // If selected customer exists, move it to the top (remove from current position first)
          const selectedIndex = items.findIndex(
            (customer: any) => customer.zoho_customerid === selectedCustomerId
          );
          if (selectedIndex >= 0) {
            // Remove from current position and prepend to top
            const [selectedCustomer] = items.splice(selectedIndex, 1);
            items = [selectedCustomer, ...items];
          }
        }
      }

      const result = {
        items,
        hasMore: response.pagination?.has_more || false,
      };

      // Cache the BASE results (without selected item prepended)
      if (shouldCache) {
        setCached("customers", cacheKey, {
          items: response.data || [],
          hasMore: response.pagination?.has_more || false,
        });
      }

      return result;
    } catch (error) {
      console.error("Error fetching customers:", error);
      return {
        items: [],
        hasMore: false,
      };
    }
  };

  const handleReferenceNumberChange = (refNum: string) => {
    setReferenceNumber(refNum);
    onReferenceNumberChange(refNum);
  };

  return (
    <div className="flex w-full flex-col items-start gap-4">
      <div className="flex w-full flex-col items-start gap-2">
        <div className="flex w-full items-center gap-4">
          <div className="flex flex-col items-start gap-2 flex-1">
            <span className="text-body-bold font-body-bold text-default-font">
              Date <span className="text-error-600">*</span>
            </span>
            <TextField className="w-full" label="" helpText="">
              <TextField.Input
                type="date"
                value={date}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleDateChange(event.target.value)
                }
              />
            </TextField>
          </div>
          <div className="flex flex-col items-start gap-2 flex-1">
            <span className="text-body-bold font-body-bold text-default-font">
              Received Via <span className="text-error-600">*</span>
            </span>
            <Select
              label=""
              helpText=""
              value={paymentMode}
              onValueChange={handlePaymentModeChange}
            >
              <Select.Item value="Bank Remittance">Bank Remittance</Select.Item>
              <Select.Item value="Bank Transfer">Bank Transfer</Select.Item>
              <Select.Item value="Cash">Cash</Select.Item>
              <Select.Item value="Cheque">Cheque</Select.Item>
              <Select.Item value="Credit Card">Credit Card</Select.Item>
              <Select.Item value="UPI">UPI</Select.Item>
            </Select>
          </div>
        </div>
        <div className="flex w-full items-center gap-4">
          <div className="flex flex-col items-start gap-2 flex-1">
            <span className="text-body-bold font-body-bold text-default-font">
              From Account <span className="text-error-600">*</span>
            </span>
            <BankGlAccountDropdown
              preset="deposit_from_other"
              bankAccountId={bankAccountId}
              selectedAccountId={fromAccount}
              selectedAccountName={selectedAccountName}
              onSelect={handleFromAccountChange}
              placeholder="Select account"
            />
          </div>
          <div className="flex flex-col items-start gap-2 flex-1">
            <span className="text-body-bold font-body-bold text-default-font">
              Received From
            </span>
            <SearchableDropdown
              fetchItems={(searchTerm, limit, offset) =>
                fetchCustomers(
                  searchTerm,
                  limit,
                  offset,
                  receivedFrom,
                  selectedCustomerName !== "Select customer (optional)"
                    ? selectedCustomerName
                    : undefined
                )
              }
              trigger={
                <Button
                  className="w-full justify-between"
                  variant="neutral-secondary"
                  iconRight={<FeatherChevronDown />}
                >
                  {selectedCustomerName}
                </Button>
              }
              getSearchableText={(customer) => customer.customer_name}
              renderItem={(customer) => customer.customer_name}
              getItemKey={(customer) => customer.zoho_customerid}
              onSelect={handleReceivedFromChange}
              selectedKey={receivedFrom}
              searchPlaceholder="Search customers..."
              emptyMessage="No customers found"
              noResultsMessage="No customers match your search"
              initialLimit={20}
            />
          </div>
        </div>
        <div className="flex w-full items-center gap-4">
          <div className="flex flex-col items-start gap-2 flex-1">
            <span className="text-body-bold font-body-bold text-default-font">
              Reference #
            </span>
            <TextField className="w-full" label="" helpText="">
              <TextField.Input
                placeholder="Enter reference number (optional)"
                value={referenceNumber}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleReferenceNumberChange(event.target.value)
                }
              />
            </TextField>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDepositFromOtherAccountsReco;
